import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { SITE } from "../config";
import { Logo } from "./Logo";

type DropdownItem = {
  to: string;
  label: string;
  description: string;
};

type NavEntry =
  | { label: string; to: string; children?: never }
  | { label: string; to?: never; children: DropdownItem[] };

const navigation: NavEntry[] = [
  {
    label: "Product",
    children: [
      {
        to: "/features",
        label: "Features",
        description: "Everything ZopDesk does today",
      },
      {
        to: "/integrations",
        label: "Integrations",
        description: "Email, WhatsApp, Acefone, Shopify, AI",
      },
      {
        to: "/security",
        label: "Security",
        description: "How your data is kept apart and encrypted",
      },
    ],
  },
  {
    label: "Solutions",
    children: [
      {
        to: "/solutions/dtc",
        label: "Direct-to-consumer",
        description: "Shopify orders next to every ticket",
      },
      {
        to: "/solutions/b2b",
        label: "B2B & wholesale",
        description: "Companies, SLA targets, your account data",
      },
      {
        to: "/solutions/marketplaces",
        label: "Marketplaces",
        description: "Queues, rules and policy replies",
      },
      {
        to: "/solutions/subscription",
        label: "Subscription",
        description: "Plan details and ticket actions",
      },
    ],
  },
  { label: "In use", to: "/customers" },
  { label: "Pricing", to: "/pricing" },
  { label: "About", to: "/about" },
];

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M3 4.5l3 3 3-3" />
    </svg>
  );
}

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    setMenuOpen(false);
    setMobileExpanded(null);
    setOpenDropdown(null);
  }, [location.pathname]);

  const isGroupActive = (children: DropdownItem[]) =>
    children.some((c) => location.pathname.startsWith(c.to));

  const handleMouseEnter = (label: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpenDropdown(label);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  return (
    <header className="sticky top-0 z-40 bg-ink-900/95 backdrop-blur">
      <div
        aria-hidden
        className="h-[3px] bg-gradient-to-r from-brand-600 via-violet-500 to-brand-600"
      />
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
        <Link
          to="/"
          aria-label={`${SITE.productName} home`}
          className="rounded-md outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900"
          onClick={() => setMenuOpen(false)}
        >
          <Logo variant="dark" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-0.5 lg:flex">
          {navigation.map((entry) =>
            entry.children ? (
              <div
                key={entry.label}
                className="relative"
                onMouseEnter={() => handleMouseEnter(entry.label)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  type="button"
                  className={`inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition ${
                    isGroupActive(entry.children)
                      ? "bg-white/10 text-white"
                      : "text-ink-300 hover:bg-white/10 hover:text-white"
                  }`}
                  aria-expanded={openDropdown === entry.label}
                >
                  {entry.label}
                  <ChevronDown
                    className={`transition-transform duration-200 ${
                      openDropdown === entry.label ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <div
                  className={`absolute left-0 top-full z-50 pt-2 transition-all duration-150 ${
                    openDropdown === entry.label
                      ? "pointer-events-auto translate-y-0 opacity-100"
                      : "pointer-events-none -translate-y-1 opacity-0"
                  }`}
                >
                  <div className="w-64 rounded-lg border border-white/10 bg-ink-800 py-1.5 shadow-xl shadow-black/30">
                    {entry.children.map((child) => (
                      <Link
                        key={child.to}
                        to={child.to}
                        className={`block px-4 py-2.5 transition hover:bg-white/5 ${
                          location.pathname.startsWith(child.to)
                            ? "bg-white/5"
                            : ""
                        }`}
                      >
                        <span className="block text-sm font-medium text-ink-100">
                          {child.label}
                        </span>
                        <span className="block text-xs text-ink-400">
                          {child.description}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={entry.label}
                to={entry.to}
                className={`rounded-md px-3 py-2 text-sm font-medium transition ${
                  location.pathname === entry.to
                    ? "bg-white/10 text-white"
                    : "text-ink-300 hover:bg-white/10 hover:text-white"
                }`}
              >
                {entry.label}
              </Link>
            ),
          )}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={SITE.appUrl}
            className="text-sm font-medium text-ink-300 transition hover:text-white"
          >
            Sign in
          </a>
          <a
            href={SITE.appUrl}
            className="rounded-md bg-gradient-to-br from-brand-500 to-violet-500 px-3.5 py-2 text-sm font-medium text-white shadow-md shadow-brand-500/30 transition hover:from-brand-400 hover:to-violet-400 hover:shadow-lg hover:shadow-brand-500/40"
          >
            Start free
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          className="inline-flex items-center justify-center rounded-md p-2 text-ink-300 hover:bg-white/10 hover:text-white lg:hidden"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          >
            {menuOpen ? (
              <>
                <path d="M4 4l12 12" />
                <path d="M16 4L4 16" />
              </>
            ) : (
              <>
                <path d="M3 6h14" />
                <path d="M3 10h14" />
                <path d="M3 14h14" />
              </>
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="border-t border-white/10 bg-ink-900 lg:hidden">
          <div className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-3 sm:px-6">
            {navigation.map((entry) =>
              entry.children ? (
                <div key={entry.label}>
                  <button
                    type="button"
                    onClick={() =>
                      setMobileExpanded((v) =>
                        v === entry.label ? null : entry.label,
                      )
                    }
                    className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition ${
                      isGroupActive(entry.children)
                        ? "bg-white/10 text-white"
                        : "text-ink-300 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    {entry.label}
                    <ChevronDown
                      className={`transition-transform duration-200 ${
                        mobileExpanded === entry.label ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {mobileExpanded === entry.label && (
                    <div className="ml-3 mt-1 flex flex-col gap-0.5 border-l border-white/10 pl-3">
                      {entry.children.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          onClick={() => setMenuOpen(false)}
                          className={`rounded-md px-3 py-2 text-sm transition ${
                            location.pathname.startsWith(child.to)
                              ? "bg-white/5 text-white"
                              : "text-ink-400 hover:bg-white/5 hover:text-ink-100"
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={entry.label}
                  to={entry.to}
                  onClick={() => setMenuOpen(false)}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition ${
                    location.pathname === entry.to
                      ? "bg-white/10 text-white"
                      : "text-ink-300 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {entry.label}
                </Link>
              ),
            )}
            <a
              href={SITE.appUrl}
              className="rounded-md px-3 py-2 text-sm font-medium text-ink-300 hover:bg-white/10 hover:text-white"
            >
              Sign in
            </a>
            <a
              href={SITE.appUrl}
              className="mt-1 rounded-md bg-gradient-to-br from-brand-500 to-violet-500 px-3 py-2 text-center text-sm font-medium text-white hover:from-brand-400 hover:to-violet-400"
            >
              Start free
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
